/**
 * Authentication context using JWT token from URL
 *
 * Provides authentication state and user data extracted from JWT token.
 * Token is passed via URL query parameter (?token=...)
 * In mock mode, requires the mock JWT token in the URL for client demos.
 */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-console */

import { createContext } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import type { TokenData } from '@ox/visits-ui/types';
import { setAuthToken } from '@ox/visits-ui/clients/mtx';
import { MOCK_MODE_ENABLED } from '@ox/visits-ui/config/mockMode';
import { MOCK_JWT_TOKEN } from '@ox/visits-ui/config/auth';
import { getAuthDataFromURL, parseJWT, validateTokenData } from '@ox/visits-ui/utils/tokenAuth';
import { useLocation } from 'preact-iso';

/**
 * Authentication context value interface
 *
 * @property isAuthenticated - Whether user has valid token
 * @property userData - User data from token (null if not authenticated)
 * @property token - Raw JWT token string (null if not authenticated)
 * @property isLoading - Whether auth initialization is in progress
 * @property error - Authentication error message (null if no error)
 */
interface AuthContextValue {
  isAuthenticated: boolean;
  userData: TokenData | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to access authentication context
 *
 * @returns Authentication context value
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { isAuthenticated, userData } = useAuth();
 *
 *   if (!isAuthenticated || !userData) {
 *     return <ErrorPage />;
 *   }
 *
 *   return <div>Welcome, {userData.first_name}!</div>;
 * }
 * ```
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: unknown;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState<TokenData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const initAuth = async () => {
      // Reset all state on each navigation so stale values don't leak across routes
      setError(null);
      setIsAuthenticated(false);
      setUserData(null);
      setToken(null);
      setIsLoading(true);

      try {
        // Mock mode: Check for token in URL, auto-add mock token if missing
        if (MOCK_MODE_ENABLED) {
          console.log('[Auth] Mock mode enabled');

          // Check if token exists in URL
          const urlParams = new URLSearchParams(window.location.search);
          const urlToken = urlParams.get('token');

          if (!urlToken) {
            // Auto-add mock JWT token to URL for demo
            console.log('[Auth] No token in URL, adding mock JWT token');
            urlParams.set('token', MOCK_JWT_TOKEN);
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
          }

          // Parse the token WITHOUT signature verification. Mock tokens carry a
          // fake signature, so verifyJWT() (used in production) would reject them.
          const finalToken = urlToken || MOCK_JWT_TOKEN;
          const parsedPayload = parseJWT(finalToken);
          const tokenData = parsedPayload ? validateTokenData(parsedPayload) : null;

          if (!tokenData) {
            setError('Failed to parse mock JWT token');
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }

          setUserData(tokenData);
          setToken(finalToken);
          setIsAuthenticated(true);
          setAuthToken(finalToken);
          console.log('[Auth] Mock auth successful:', tokenData.user_email);
          setIsLoading(false);
          return;
        }

        // Production mode: Parse token from URL
        const tokenData = await getAuthDataFromURL();

        if (!tokenData) {
          setError('Invalid or missing authentication token');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Get raw token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const rawToken = urlParams.get('token');

        if (rawToken) {
          setToken(rawToken);
          setUserData(tokenData);
          setIsAuthenticated(true);
          setAuthToken(rawToken);
          console.log('[Auth] Token auth successful:', tokenData.user_email);
        } else {
          setError('Token missing from URL');
          setIsAuthenticated(false);
        }

        setIsLoading(false);

        if (tokenData.exp < Date.now() / 1000) {
          location.route('/expired');
        }
      } catch (err: unknown) {
        console.error('[Auth] Authentication failed:', err);
        setError(err.message || 'Authentication failed');
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    initAuth();
  }, [location]);

  const value: AuthContextValue = {
    isAuthenticated,
    userData,
    token,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
